import naclutil from 'tweetnacl-util';
import Item from '../models/Item';
import Order from '../models/Order';
import Card from '../models/Card';
import Billing from '../models/Billing';
import PaymentLocation from '../models/PaymentLocation'
import Helpers from '../base/Helpers';
import RequestBehaviour from '../base/RequestBehaviour';
import FailedEncryptionException from '../exceptions/FailedEncryptionException';

export default class PaymentTransaction extends RequestBehaviour {
	/**
	 * Payment UUID
	 */
	payment_uuid?: string = undefined;

	/**
	 * Tokenized card identifier (T-* format)
	 */
	card_token?: string | null = undefined;

	/**
	 * Card number or PAN
	 */
	card_number?: string | null = undefined;

	/**
	 * Card security code
	 */
	card_cvv?: string | null = undefined;

	/**
	 * Card expire year/month date (YYMM)
	 */
	card_expire?: string | null = undefined;

	/**
	 * Cardholder name
	 */
	card_holder?: string | null = undefined;

	/**
	 * Customer billing address
	 */
	billing_address?: string | null = undefined;

	/**
	 * Customer billing country alpha-2 code (ISO 3166-1)
	 */
	billing_country?: string = undefined;

	/**
	 * Customer billing state alpha code (ISO 3166-2)
	 */
	billing_state?: string = undefined;

	/**
	 * Customer billing city
	 */
	billing_city?: string | null = undefined;

	/**
	 * Customer billing postal code
	 */
	billing_zip?: string = undefined;

	/**
	 * Customer billing phone
	 */
	billing_phone?: string = undefined;

	/**
	 * Order customer name
	 */
	customer_name?: string | null = undefined;

	/**
	 * Order customer email
	 */
	customer_email?: string | null = undefined;

	/**
	 * Order customer identification number
	 */
	customer_identification?: string | null = undefined;

	/**
	 * Order customer device fingerprint
	 */
	customer_fingerprint?: string = undefined;

	/**
	 * Order ID
	 */
	order_id?: string = undefined;

	/**
	 * Order currency code alpha-3
	 */
	order_currency?: string = undefined;

	/**
	 * Order total amount
	 */
	order_amount?: string | null = undefined;

	/**
	 * Order total tax amount
	 */
	order_tax_amount?: string | null = undefined;

	/**
	 * Order total shipping amount
	 */
	order_shipping_amount?: string | null = undefined;

	/**
	 * Order tip shipping amount
	 */
	order_tip_amount?: string | null = undefined;

	/**
	 * Order summary of items or products
	 */
	order_content?: Array<Item> = [];

	/**
	 * Order extra properties
	 */
	order_extras?: Object = {};

	/**
	 * Order note or aditional instructions
	 */
	order_note?: string | null = undefined;

	/**
	 * Order calback webhook URL
	 */
	order_callback?: string = undefined;

	/**
	 * Activate authentication request (3DS/EMV)
	 */
	authentication_request: boolean = false;

	/**
	 * Authentication transaction identifier
	 */
	authentication_identifier?: string = undefined;

	/**
	 * Secure payload for transactions
	 */
	secure_payload?: string = undefined;

	/**
	 * Wallet payload for Apple Pay transactions
	 */
	apple_pay?: string = undefined;

	/**
	 * Wallet payload for Google Pay transactions
	 */
	google_pay?: string = undefined;

	/**
	 * Wallet payload for Click to Pay transactions
	 */
	click_to_pay?: string = undefined;

	/**
	 * Tap to pay device serial code
	 */
	tap_to_pay_device?: string = undefined;

	/**
	 * Activate Tap to Pay Request transaction
	 */
	tap_to_pay_request?: boolean = undefined;

	/**
	 * Set the payment coordinates
	 */
	payment_location?: PaymentLocation;

	/**
	 * Callback to execute before authenticating a transaction
	 */
	before_authenticate?: () => Promise<string | undefined> = undefined;

	/**
	 * Callback to execute before executing or retrying a transaction
	 */
	before_execute?: () => Promise<void> = undefined;

	/**
	 * Associate and mapping Card model properties to transaction
	 *
	 * @param card
	 */
	setCard(card: Card) {
		this.card_number = Helpers.cleanString(card.number);
		this.card_cvv = card.cvv2;
		this.card_expire = card.getExpireFormat();
		this.card_holder = Helpers.trimValue(card.cardholder);
	}

	/**
	 * Associate and mapping CardToken model properties to transaction
	 *
	 * @param token
	 */
	setCardToken(token: string | null = null) {
		this.card_token = token;
	}

	/**
	 * Associate and mapping Billing model properties to transaction
	 *
	 * @param billing
	 */
	setBilling(billing: Billing) {
		this.billing_address = Helpers.trimValue(billing.address);
		this.billing_country = billing.country;
		this.billing_state = billing.state;
		this.billing_city = Helpers.trimValue(billing.city);
		this.billing_zip = billing.zip;
		this.billing_phone = billing.phone;
	}

	/**
	 * Associate and mapping Order model properties to transaction
	 *
	 * @param order
	 */
	setOrder(order: Order) {
		this.order_id = order.id;
		this.order_currency = order.currency;
		this.order_amount = Helpers.parseAmount(order.amount);
		this.order_tax_amount = Helpers.parseAmount(order.tax_amount);
		this.order_shipping_amount = Helpers.parseAmount(order.shipping_amount);
		this.order_tip_amount = Helpers.parseAmount(order.tip_amount);
		this.order_content = order.content;
		this.order_extras = order.extras;
		this.order_note = Helpers.trimValue(order.note);
		this.order_callback = order.callback_url;

		this.customer_name = Helpers.trimValue(order.customer_name);
		this.customer_email = order.customer_email;
		this.customer_identification = order.customer_identification;
	}

	/**
	 * Enable 3DS/EMV authentication request
	 */
	withAuthenticationRequest() {
		this.authentication_request = true;
	}

	/**
	 * Enable Tap to Pay request
	 */
	withTapToPay(device?: string) {
		this.tap_to_pay_request = true;

		if (device) {
			this.tap_to_pay_device = device;
		}
	}

	/**
	 * Set the payment location latitude and longitude coordinate values for payment
	 * @param latitude
	 * @param longitude
	 */
	public setPaymentLocation(latitude: number, longitude: number): void {
		this.payment_location = {latitude, longitude};
	}

	/**
	 * Set callback before authenticating transaction
	 *
	 * @param callback
	 */
	setBeforeAuthenticate(callback: () => Promise<string | undefined>) {
		this.before_authenticate = callback;
	}

	/**
	 * Set callback before executing transaction
	 *
	 * @param callback
	 */
	setBeforeExecute(callback: () => Promise<void>) {
		this.before_execute = callback;
	}

	/**
	 * Run callback before executing transaction
	 */
	async doBeforeExecute() {
		if (this.before_execute) {
			await this.before_execute();
		}
	}

	/**
	 * Check if current request needs encryption.
	 *
	 * @return
	 */
	isEncryptable(): boolean {
		return Boolean(
			this.card_number
			|| this.card_cvv
			|| this.card_expire
			|| this.card_holder
			|| this.customer_name
			|| this.customer_email
			|| this.customer_identification
		);
	}

	/**
	 * Encrypt fields with sensitive data.
	 *
	 * @param publicKey
	 * @return
	 * @throws FailedEncryptionException
	 */
	withEncryption(publicKey: string | null): string {
		if (!publicKey) {
			throw new FailedEncryptionException('Could not process transaction without merchant public key.');
		}

		try {
			const keyPair = this.sdk_key_pair ?? Helpers.getKeyPair();

			if (this.card_number) this.card_number = Helpers.encrypt(this.card_number, publicKey, keyPair);
			if (this.card_cvv) this.card_cvv = Helpers.encrypt(this.card_cvv, publicKey, keyPair);
			if (this.card_expire) this.card_expire = Helpers.encrypt(this.card_expire, publicKey, keyPair);
			if (this.card_holder) this.card_holder = Helpers.encrypt(this.card_holder, publicKey, keyPair);
			if (this.customer_email) this.customer_email = Helpers.encrypt(this.customer_email, publicKey, keyPair);
			if (this.customer_name) this.customer_name = Helpers.encrypt(this.customer_name, publicKey, keyPair);
			if (this.customer_identification) this.customer_identification = Helpers.encrypt(this.customer_identification, publicKey, keyPair);

			const sdk_public_key = naclutil.encodeBase64(keyPair.publicKey);

			this.sdk_key_pair = keyPair;

			return sdk_public_key;
		} catch (error) {
			throw new FailedEncryptionException('Encryption process encountered an unexpected error.');
		}
	}

	/**
	 * Get the transaction request type
	 *
	 * @returns
	 */
	getRequestType(): string {
		return '';
	}
}
